import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, LayoutGrid, List, MoreHorizontal, ChevronDown, Check, Star, Mail, Calendar, MessageSquare, X, ArrowDown, Pencil, MessageCircle, Trash2, ArrowUp, Loader2, Phone, MonitorSmartphone } from 'lucide-react';
import { supabase, supabase_get_vacancy_applications, supabase_update_application_status, supabase_delete_application, supabase_get_templates, supabase_log_interaction } from '../services/supabaseService';
import { triggerAutomation } from '../services/automationService';
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { UserRole, BULLHORN_STATUS_OPTIONS } from '../types';
import CandidateDetailModal from './CandidateDetailModal';
import BulkEmailComposerModal, { BulkCandidate } from './BulkEmailComposerModal';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import CandidateMoveModal from './CandidateMoveModal';
import { VOORGESTELD_RESUME_BUNDLE_KEY, RESUME_VOORGESTELD_MOVE_MODAL_EVENT, resumeBundleMatchesVacancyEvent } from '../utils/voorgesteldResumeNavigation';
import { isPortalBlockCandidateDetailForStatus, isPortalHideCandidateNameForStatus } from '../utils/clientPortalGuards';
import { isClientPortalHostname } from '../utils/isClientPortalHost';
import { searchTextIncludes } from '../utils/searchText';
import { getCandidateEmail } from '../utils/candidateContact';

const SHORTLIST_STAGE_ID =
    DEFAULT_KANBAN_STAGES.find((s) => s.name === 'Shortlist')?.id || 'Shortlist';

/** Map legacy DB values (New, Nieuw, …) to the canonical pipeline stage id used in the UI. */
function normalizeVacancyApplicationStageId(status: string | null | undefined): string {
    const raw = (status || '').trim();
    if (!raw) return SHORTLIST_STAGE_ID;
    const lower = raw.toLowerCase();
    if (['new', 'nieuw', 'status_new', 'op shortlist', 'screening', 'lead'].includes(lower)) {
        return SHORTLIST_STAGE_ID;
    }
    const byId = DEFAULT_KANBAN_STAGES.find((s) => s.id === raw);
    if (byId) return byId.id;
    const byName = DEFAULT_KANBAN_STAGES.find((s) => s.name === raw);
    if (byName) return byName.id;
    return raw;
}

function displayVacancyApplicationStage(status: string | null | undefined): string {
    const id = normalizeVacancyApplicationStageId(status);
    return DEFAULT_KANBAN_STAGES.find((s) => s.id === id)?.name || id;
}

const VOORGESTELD_STAGE_ID =
    DEFAULT_KANBAN_STAGES.find((s) => s.name === 'Voorgesteld')?.id || 'Voorgesteld';

/** Alleen echte Voorgesteld/voorstel-fase (niet Shortlist of latere kolommen). */
function isApplicationVoorgesteldStatus(status: string | null | undefined): boolean {
    if (normalizeVacancyApplicationStageId(status) === VOORGESTELD_STAGE_ID) return true;
    const raw = String(status || '').trim();
    if (!raw) return false;
    const lower = raw.toLowerCase();
    if (lower === 'voorstellen') return false;
    if (/voorgesteld|voorgedragen/.test(lower)) return true;
    return /\bvoorstel\b/.test(lower) || lower === 'voorstel';
}
interface VacancyFiltersTabProps {
    vacancyId: string;
    userRole?: UserRole;
    onAddCandidate?: () => void;
}

const VacancyFiltersTab: React.FC<VacancyFiltersTabProps> = ({ vacancyId, userRole, onAddCandidate }) => {
    const [candidates, setCandidates] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUserName, setCurrentUserName] = useState<string>('Onbekend');
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [showDebug, setShowDebug] = useState(false);
    
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState({ qualified: true, disqualified: false, new: true, overdue: false });
    const [stageFilter, setStageFilter] = useState<string>('');
    const [sourceTagFilter, setSourceTagFilter] = useState<string>('');
    const [tagFilter, setTagFilter] = useState<string>('');
    
    // UI State
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);
    const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
    const [showBulkWhatsAppModal, setShowBulkWhatsAppModal] = useState(false);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
    const [candidateAction, setCandidateAction] = useState<'whatsapp' | 'email' | undefined>(undefined);
    const [showStats, setShowStats] = useState(false);
    const [sortConfig, setSortConfig] = useState({ column: 'created_at', ascending: false });
    
    const [templates, setTemplates] = useState<any[]>([]);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveDetails, setMoveDetails] = useState<any>(null);

    useEffect(() => {
        loadCandidates();
        loadTemplates();
    }, [vacancyId]);

    const loadTemplates = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', user.id).single();
                if (profile?.client_id) {
                    const { data: clientData } = await supabase.from('clients').select('data').eq('id', profile.client_id).single();
                    if (clientData?.data?.email_templates) {
                        setTemplates(clientData.data.email_templates);
                        return;
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
        // Fallback
        const t = await supabase_get_templates();
        setTemplates(t || []);
    };

    const loadCandidates = async () => {
        setIsLoading(true);
        try {
            // Debug: fetch raw vacancy info to verify IDs
            const { data: vacRow } = await supabase.from('vacancies').select('id, bullhorn_id, title').eq('id', vacancyId).single();

            // Count applications across all duplicates with same bullhorn_id
            let dupeIds: string[] = [vacancyId];
            if (vacRow?.bullhorn_id) {
                const { data: dupes } = await supabase.from('vacancies').select('id').eq('bullhorn_id', vacRow.bullhorn_id);
                if (dupes) dupeIds = dupes.map((d: any) => d.id);
            }
            const { count: rawCount } = await supabase.from('applications').select('id', { count: 'exact', head: true }).in('vacancy_id', dupeIds);

            const apps = await supabase_get_vacancy_applications(vacancyId);
            setCandidates(apps || []);

            setDebugInfo({
                vacancyId,
                bullhornId: vacRow?.bullhorn_id || 'geen',
                title: vacRow?.title || '?',
                duplicateIds: dupeIds,
                rawAppCount: rawCount ?? 0,
                loadedAppCount: apps?.length ?? 0,
                sampleVacancyIds: (apps || []).slice(0, 3).map((a: any) => a.vacancy_id),
            });

            // Get current user name
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const name = user.user_metadata?.name || user.user_metadata?.full_name || user.email;
                if (name) setCurrentUserName(name);
            }
        } catch(e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenMoveModal = (cand: any) => {
        const stageId = normalizeVacancyApplicationStageId(cand.status);
        const vd = cand.vacancies?.data || {};
        const defaultClientEmail = String(
            vd.contactEmail ||
                vd.clientContactEmail ||
                vd.contactPersonEmail ||
                vd.email ||
                vd.hiringManagerEmail ||
                ''
        ).trim();
        setMoveDetails({
            applicationId: cand.id,
            candidateId: cand.candidate_id,
            candidateRealId: cand.candidate_id,
            vacancyId: vacancyId,
            candidateName: cand.candidates?.name || 'Kandidaat',
            candidateEmail: getCandidateEmail(cand.candidates),
            fromStage: stageId,
            toStage: stageId,
            clientId: cand.client_id,
            vacancyTitle: cand.vacancies?.title || cand.vacancies?.data?.title || '',
            clientName: cand.vacancies?.eindklant?.name || cand.vacancies?.opdrachtgever?.name || cand.vacancies?.data?.clientName || cand.source_client?.name || '',
            defaultClientEmail: defaultClientEmail || undefined,
        });
        setShowMoveModal(true);
    };

    const handleOpenVoorstelVenster = (cand: any) => {
        const stageId = normalizeVacancyApplicationStageId(cand.status);
        const vd = cand.vacancies?.data || {};
        const defaultClientEmail = String(
            vd.contactEmail ||
                vd.clientContactEmail ||
                vd.contactPersonEmail ||
                vd.email ||
                vd.hiringManagerEmail ||
                ''
        ).trim();
        setMoveDetails({
            applicationId: cand.id,
            candidateId: cand.candidate_id,
            candidateRealId: cand.candidate_id,
            vacancyId: vacancyId,
            candidateName: cand.candidates?.name || 'Kandidaat',
            candidateEmail: getCandidateEmail(cand.candidates),
            fromStage: stageId,
            toStage: 'Voorgesteld',
            clientId: cand.client_id,
            vacancyTitle: cand.vacancies?.title || cand.vacancies?.data?.title || '',
            clientName: cand.vacancies?.eindklant?.name || cand.vacancies?.opdrachtgever?.name || cand.vacancies?.data?.clientName || cand.source_client?.name || '',
            defaultClientEmail: defaultClientEmail || undefined,
        });
        setShowMoveModal(true);
    };

    useEffect(() => {
        const fn = (ev: Event) => {
            const d = (ev as CustomEvent<{ vacancyId?: string; applicationId?: string }>).detail;
            if (!d?.vacancyId || d.vacancyId !== vacancyId) return;
            try {
                const raw = sessionStorage.getItem(VOORGESTELD_RESUME_BUNDLE_KEY);
                if (!raw) return;
                const b = JSON.parse(raw);
                if (!resumeBundleMatchesVacancyEvent(b, d)) return;
                if (!b.move) return;
                setMoveDetails(b.move);
                setShowMoveModal(true);
            } catch {
                /* ignore */
            }
        };
        window.addEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
        return () => window.removeEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
    }, [vacancyId]);

    const handleConfirmShortlistMove = async (channel: 'email' | 'whatsapp' | 'log', templateId: string, subject: string, messageContent: string, internalNoteContent: string, targetStage: string, asDraft: boolean = false, rejectionReason?: string, cc?: string, bcc?: string, fromEmail?: string, _voorgesteld?: import('../utils/portalProposalThread').VoorgesteldClientPayload | null) => {
        if (!moveDetails) return;
        try {
            const targetStageObj = DEFAULT_KANBAN_STAGES.find(s => s.name === targetStage || s.id === targetStage) || { id: targetStage, name: targetStage };

            // 1. Update Application Status
            await supabase_update_application_status(moveDetails.applicationId, targetStageObj.id);

            // 2. Trigger Automation
            const { data: { user } } = await supabase.auth.getUser();
            const autoPayload = {
                vacancyId: moveDetails.vacancyId,
                candidateId: moveDetails.candidateRealId,
                stageId: targetStageObj.id,
                stageName: targetStageObj.name,
                user_id: user?.id
            };
            await triggerAutomation('stage_changed', autoPayload);

            // 3. Communications
            if (channel !== 'log') {
                await supabase.from('todos').insert({
                    title: `[OUTBOX] Bericht aan ${moveDetails.candidateName}`,
                    description: messageContent,
                    status: 'Pending',
                    due_date: new Date().toISOString(),
                    user_id: user?.id,
                    assignee_id: user?.id
                });
                alert('Bericht is opgeslagen in je Postvak UIT (Recruiter Hub).');
            }

            if (moveDetails.clientId && user) {
                const nameStr = user.user_metadata?.name || user.user_metadata?.full_name || 'Recruiter';
                
                // Log system movement
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    moveDetails.clientId,
                    'system',
                    `(Status Update) ${nameStr} heeft kandidaat verplaatst naar '${targetStage}'.`
                );

                if (internalNoteContent && internalNoteContent.trim()) {
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        moveDetails.clientId,
                        'note',
                        `[Verplaatst naar ${targetStage}] ${internalNoteContent}`
                    );
                }

                if (channel !== 'log' && messageContent) {
                    const logType = channel === 'whatsapp' ? 'whatsapp' : 'email';
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        moveDetails.clientId,
                        logType,
                        `[Inkomend/Uitgaand: ${targetStage}] ${messageContent}`
                    );
                }
            }

            setCandidates(candidates.map(c => c.id === moveDetails.applicationId ? { ...c, status: targetStageObj.id } : c));
            setShowMoveModal(false);
            setMoveDetails(null);
            
            // Recount or refresh if needed, but optimistic update is usually fine.
        } catch (err: any) {
            console.error(err);
            alert("Er is iets misgegaan bij het verplaatsen.");
        }
    };

    const handleRemoveApplication = async (applicationId: string, candidateName: string) => {
        if (window.confirm(`Weet je zeker dat je ${candidateName} van de shortlist wilt verwijderen?`)) {
            try {
                await supabase_delete_application(applicationId);
                setCandidates(candidates.filter(c => c.id !== applicationId));
            } catch (error) {
                console.error(error);
                alert("Error removing application");
            }
        }
    };

    // Filter logic
    let filteredCandidates = candidates.filter(cand => {
        // Status filter
        if (cand.isDeleted && !statusFilter.disqualified) return false;
        if (!cand.isDeleted && !statusFilter.qualified && !statusFilter.new) return false;

        // Stage filter
        if (stageFilter && normalizeVacancyApplicationStageId(cand.status) !== stageFilter) return false;

        // Search
        if (searchQuery) {
            const candName = cand.candidates?.name || '';
            const candEmail = cand.candidates?.email || '';
            const candPhone = cand.candidates?.phone || '';
            const matchesTag =
                cand.tags && Array.isArray(cand.tags)
                    ? cand.tags.some((t: string) => searchTextIncludes(t, searchQuery))
                    : false;
            if (
                !searchTextIncludes(candName, searchQuery) &&
                !searchTextIncludes(candEmail, searchQuery) &&
                !searchTextIncludes(candPhone, searchQuery) &&
                !matchesTag
            ) {
                return false;
            }
        }

        // Tag filter
        if (tagFilter) {
            if (!cand.tags || !Array.isArray(cand.tags) || !cand.tags.includes(tagFilter)) return false;
        }

        // Source filter
        if (sourceTagFilter) {
            const sourceName = cand.source_client?.name?.toLowerCase() || 'werfplaats';
            if (!sourceName.includes(sourceTagFilter.toLowerCase())) return false;
        }

        return true;
    });

    const isClientPortal = isClientPortalHostname();

    const openCandidateDetailIfAllowed = (cand: any) => {
        if (isClientPortal && isPortalBlockCandidateDetailForStatus(cand?.status, cand)) {
            window.dispatchEvent(
                new CustomEvent('wt:toast', {
                    detail: {
                        message: 'CV en kandidaatdetails zijn in deze fase niet beschikbaar in het clientportaal.',
                        type: 'error',
                    },
                }),
            );
            return;
        }
        setSelectedCandidateId(cand.candidate_id);
    };

    filteredCandidates = filteredCandidates.map(cand => {
        const s = (cand.status || 'New').toLowerCase();
        if (isClientPortal && isPortalHideCandidateNameForStatus(cand.status, cand)) {
            return {
                ...cand,
                candidates: {
                    ...cand.candidates,
                    name: 'Werfplaats potential',
                    email: 'verborgen',
                    phone: 'verborgen',
                    photo_url: null,
                    avatar_url: null
                }
            };
        }
        return cand;
    });

    // Sort logic
    filteredCandidates = filteredCandidates.sort((a, b) => {
        if (sortConfig.column === 'candidate') {
            const valA = (a.candidates?.name || '').toLowerCase();
            const valB = (b.candidates?.name || '').toLowerCase();
            return sortConfig.ascending ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        if (sortConfig.column === 'created_at') {
            const valA = new Date(a.created_at).getTime();
            const valB = new Date(b.created_at).getTime();
            return sortConfig.ascending ? valA - valB : valB - valA;
        }
        return 0;
    });

    const counts = {
        qualified: candidates.filter(c => !c.isDeleted).length,
        disqualified: candidates.filter(c => c.isDeleted).length,
        new: candidates.filter(c => !c.isDeleted && c.status === 'New').length,
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedCandidateIds(filteredCandidates.map(c => c.candidate_id));
        } else {
            setSelectedCandidateIds([]);
        }
    };

    const handleSelectOne = (id: string) => {
        setSelectedCandidateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    return (
        <div className="flex flex-col h-full w-full bg-slate-50 relative">
            <div className="flex-1 flex flex-col gap-6 overflow-hidden h-full px-6 lg:px-10 py-6">
                
                {/* Search & Filter Top Bar */}
                <div className="bg-white p-2 rounded-xl shadow-sm flex items-center gap-2 border border-slate-200 relative z-20">
                    <div className="w-9 h-9 flex items-center justify-center text-[#0073e6] bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setShowStats(!showStats)}>
                        <Filter size={18} />
                    </div>
                    <input
                        type="text"
                        placeholder="Zoek op naam, e-mail, telefoon of functie..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent text-sm font-semibold outline-none h-9 text-slate-800 px-3 placeholder-slate-400"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors">
                            <X size={16} />
                        </button>
                    )}
                    <div className="p-2.5 rounded-lg text-slate-400">
                        {isLoading ? <Loader2 size={18} className="animate-spin text-[#0073e6]" /> : <Search size={18} />}
                    </div>
                </div>

                {/* ── DEBUG PANEL ── */}
                {showDebug && debugInfo && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 font-mono text-[11px] space-y-1.5 shrink-0">
                        <div className="font-black text-amber-700 text-xs mb-2 flex items-center gap-2">🐛 Debug Info — Pipeline / Shortlist</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                            <span className="text-amber-600 font-bold">Vacancy UUID:</span>
                            <span className="text-slate-700 break-all">{debugInfo.vacancyId}</span>

                            <span className="text-amber-600 font-bold">Bullhorn ID:</span>
                            <span className="text-slate-700">{String(debugInfo.bullhornId)}</span>

                            <span className="text-amber-600 font-bold">Vacature titel:</span>
                            <span className="text-slate-700 truncate">{debugInfo.title}</span>

                            <span className="text-amber-600 font-bold">Duplicaat IDs gevonden:</span>
                            <span className="text-slate-700">{debugInfo.duplicateIds.length} UUID(s)</span>

                            <span className="text-amber-600 font-bold">Applications in DB (raw):</span>
                            <span className={`font-black ${debugInfo.rawAppCount > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{debugInfo.rawAppCount}</span>

                            <span className="text-amber-600 font-bold">Applications geladen:</span>
                            <span className={`font-black ${debugInfo.loadedAppCount > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{debugInfo.loadedAppCount}</span>

                            <span className="text-amber-600 font-bold">Weergegeven (na filter):</span>
                            <span className="text-slate-700 font-black">{filteredCandidates.length}</span>
                        </div>
                        {debugInfo.duplicateIds.length > 1 && (
                            <div className="mt-2 pt-2 border-t border-amber-200">
                                <span className="text-amber-600 font-bold">Alle duplicate UUIDs:</span>
                                <div className="mt-1 space-y-0.5">
                                    {debugInfo.duplicateIds.map((id: string) => (
                                        <div key={id} className={`text-[10px] break-all ${id === debugInfo.vacancyId ? 'text-indigo-600 font-bold' : 'text-slate-500'}`}>
                                            {id === debugInfo.vacancyId ? '→ ' : '  '}{id}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {debugInfo.sampleVacancyIds.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-amber-200">
                                <span className="text-amber-600 font-bold">Sample application.vacancy_id:</span>
                                {debugInfo.sampleVacancyIds.map((id: string, i: number) => (
                                    <div key={i} className="text-[10px] text-slate-500 break-all">{id}</div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Collapsible Stats & Tools Panel */}
                {showStats && (
                    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm animate-slideDown origin-top shrink-0 relative z-10">
                        <div className="flex justify-between items-center mb-5">
                            <h4 className="text-sm font-bold text-[#0073e6]">Snelle Filters</h4>
                            <button onClick={() => setShowStats(false)} className="text-slate-300 hover:text-slate-500"><X size={16} /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Status */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-slate-500">Kandidaat Status</p>
                                <div className="flex flex-wrap gap-2">
                                    <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all cursor-pointer bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300">
                                        <input type="checkbox" checked={statusFilter.qualified} onChange={(e) => setStatusFilter({...statusFilter, qualified: e.target.checked})} className="w-4 h-4 text-[#0073e6] rounded border-slate-300 focus:ring-[#0073e6]" />
                                        Gekwalificeerd <span className="text-slate-400">({counts.qualified})</span>
                                    </label>
                                    <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all cursor-pointer bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300">
                                        <input type="checkbox" checked={statusFilter.disqualified} onChange={(e) => setStatusFilter({...statusFilter, disqualified: e.target.checked})} className="w-4 h-4 text-[#0073e6] rounded border-slate-300 focus:ring-[#0073e6]" />
                                        Gediskwalificeerd <span className="text-slate-400">({counts.disqualified})</span>
                                    </label>
                                </div>
                            </div>
                            
                            {/* Fase */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-slate-500">In fase</p>
                                <select 
                                    value={stageFilter} 
                                    onChange={(e) => setStageFilter(e.target.value)}
                                    className="w-full appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-sm py-2 px-3 rounded-lg hover:border-slate-300 transition-colors cursor-pointer outline-none focus:border-[#0073e6] font-semibold shadow-sm"
                                >
                                    <option value="">Alle fases</option>
                                    {DEFAULT_KANBAN_STAGES.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Bron & Tags */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-slate-500">Bron & Tags</p>
                                <div className="flex flex-col gap-2">
                                    <select 
                                        value={sourceTagFilter} 
                                        onChange={(e) => setSourceTagFilter(e.target.value)}
                                        className="w-full appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-sm py-2 px-3 rounded-lg hover:border-slate-300 transition-colors cursor-pointer outline-none focus:border-[#0073e6] font-semibold shadow-sm"
                                    >
                                        <option value="">Kies bron tag</option>
                                        <option value="LinkedIn">LinkedIn</option>
                                        <option value="Direct">Direct</option>
                                        <option value="Referral">Referral</option>
                                    </select>
                                    <select 
                                        value={tagFilter} 
                                        onChange={(e) => setTagFilter(e.target.value)}
                                        className="w-full appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-sm py-2 px-3 rounded-lg hover:border-slate-300 transition-colors cursor-pointer outline-none focus:border-[#0073e6] font-semibold shadow-sm"
                                    >
                                        <option value="">Kies tag</option>
                                        <option value="Senior">Senior</option>
                                        <option value="Remote">Remote</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data Table Wrapper */}
                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col relative min-h-0">
                    <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center backdrop-blur-sm sticky top-0 z-10 shrink-0">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-slate-500">{filteredCandidates.length} Kandidaten in Shortlist</span>
                            <button
                                onClick={() => setShowDebug(d => !d)}
                                title="Debug info"
                                className={`text-xs px-2 py-0.5 rounded border font-mono transition-colors ${showDebug ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-slate-100 border-slate-200 text-slate-400 hover:text-amber-600'}`}
                            >
                                🐛 debug
                            </button>
                        </div>
                        <div className="flex gap-2">
                            {selectedCandidateIds.length > 0 && (
                                <>
                                    <button 
                                        onClick={() => isClientPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Upgrade portal voor mail en whatsapp, contact Werfplaats.", type: 'error' } })) : setShowBulkWhatsAppModal(true)} 
                                        className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg shadow-sm transition-all animate-fadeIn ${isClientPortal ? 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60 blur-[1px]' : 'bg-[#25D366] text-white hover:bg-[#1DA851]'}`}
                                        title={isClientPortal ? "Upgrade portal voor mail en whatsapp, contact Werfplaats." : ""}
                                    >
                                        <MessageCircle size={16} /> Samen Appen ({selectedCandidateIds.length})
                                    </button>
                                    <button 
                                        onClick={() => isClientPortal ? window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Upgrade portal voor mail en whatsapp, contact Werfplaats.", type: 'error' } })) : setShowBulkEmailModal(true)} 
                                        className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg shadow-sm transition-all animate-fadeIn ${isClientPortal ? 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60 blur-[1px]' : 'bg-[#0078D4] text-white hover:bg-[#0063B1]'}`}
                                        title={isClientPortal ? "Upgrade portal voor mail en whatsapp, contact Werfplaats." : ""}
                                    >
                                        <Mail size={16} /> Samen E-mailen ({selectedCandidateIds.length})
                                    </button>
                                </>
                            )}
                            <button onClick={onAddCandidate} className={`flex items-center gap-2 text-sm font-bold bg-[#0073e6] text-white px-4 py-2 rounded-lg shadow-sm transition-all ${isClientPortal ? 'hidden' : 'hover:bg-[#005bb5]'}`}>
                                <Plus size={16} /> Nieuwe Kandidaat
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto overflow-x-auto custom-scrollbar bg-white min-h-0">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="animate-spin text-slate-400 mb-3" size={32} />
                                <p className="text-sm font-semibold text-slate-500">Kandidaten laden...</p>
                            </div>
                        ) : filteredCandidates.length === 0 ? (
                            <div className="text-center py-20 text-slate-500 text-sm font-semibold">Geen kandidaten gevonden in de shortlist.</div>
                        ) : (
                            <table className="w-full text-left border-collapse min-w-[1000px]">
                                <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                                    <tr>
                                        <th className="px-5 py-4 w-12 border-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedCandidateIds.length === filteredCandidates.length && filteredCandidates.length > 0} 
                                                onChange={handleSelectAll} 
                                                className="w-4 h-4 rounded border-slate-300 cursor-pointer text-[#0073e6] focus:ring-[#0073e6]" 
                                            />
                                        </th>
                                        <th className="px-5 py-4 text-xs font-bold text-slate-500 select-none w-24">
                                            Acties
                                        </th>
                                        <th 
                                            className="px-5 py-4 text-xs font-bold text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                            onClick={() => setSortConfig(s => ({ column: 'candidate', ascending: s.column === 'candidate' ? !s.ascending : true }))}
                                        >
                                            <div className="flex items-center gap-1">Kandidaat {sortConfig.column === 'candidate' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                        </th>
                                        <th className="px-5 py-4 text-xs font-bold text-slate-500 hidden md:table-cell select-none">
                                            Contactgegevens
                                        </th>
                                        <th 
                                            className="px-5 py-4 text-xs font-bold text-slate-500 hidden md:table-cell cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                            onClick={() => setSortConfig(s => ({ column: 'created_at', ascending: s.column === 'created_at' ? !s.ascending : false }))}
                                        >
                                            <div className="flex items-center gap-1">Toegevoegd {sortConfig.column === 'created_at' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                        </th>
                                        <th className="px-5 py-4 text-xs font-bold text-slate-500 select-none w-48">
                                            Status
                                        </th>
                                        <th className="px-5 py-4 text-xs font-bold text-slate-500 hidden xl:table-cell text-right select-none">
                                            Bron
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredCandidates.map(cand => {
                                        const candidateName = cand.candidates?.name || 'Onbekend';
                                        const candidateEmail = cand.candidates?.email || '';
                                        const candidatePhone = cand.candidates?.phone || '';
                                        let roleDisplay = cand.candidates?.role || 'Kandidaat';
                                        if (cand.candidates?.ai_parsed_data?.work_experience?.[0]) {
                                            const exp = cand.candidates.ai_parsed_data.work_experience[0];
                                            if (exp.role && exp.company) {
                                                roleDisplay = `${exp.role} bij ${exp.company}`;
                                            } else if (exp.role) {
                                                roleDisplay = exp.role;
                                            }
                                        }

                                        return (
                                            <tr key={cand.id} className="cursor-pointer transition-all hover:bg-slate-50 group border-l-4 border-l-transparent hover:border-l-slate-300">
                                                <td className="px-5 py-4">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={selectedCandidateIds.includes(cand.candidate_id)}
                                                        onChange={() => handleSelectOne(cand.candidate_id)}
                                                        onClick={(e) => e.stopPropagation()} 
                                                        className="w-4 h-4 rounded border-slate-300 cursor-pointer text-[#0073e6] focus:ring-[#0073e6]" 
                                                    />
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isClientPortal) {
                                                                    window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Deze optie is alleen voor Werfplaats recruiters.", type: 'error' } }));
                                                                } else {
                                                                    setSelectedCandidateId(cand.candidate_id);
                                                                }
                                                            }}
                                                            className={`p-1.5 bg-white border border-slate-200 rounded shadow-sm transition-all ${isClientPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'hover:border-slate-300 hover:bg-slate-50 text-slate-500 hover:text-[#0073e6]'}`}
                                                            title={isClientPortal ? "Alleen voor Werfplaats recruiters" : "Bewerken"}
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                        {isApplicationVoorgesteldStatus(cand.status) && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isClientPortal) {
                                                                    window.dispatchEvent(
                                                                        new CustomEvent('wt:toast', {
                                                                            detail: {
                                                                                message: 'Deze optie is alleen voor Werfplaats recruiters.',
                                                                                type: 'error',
                                                                            },
                                                                        }),
                                                                    );
                                                                } else {
                                                                    handleOpenVoorstelVenster(cand);
                                                                }
                                                            }}
                                                            className={`p-1.5 bg-white border border-slate-200 rounded shadow-sm transition-all ${isClientPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'hover:border-indigo-200 hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800'}`}
                                                            title={
                                                                isClientPortal
                                                                    ? 'Alleen voor Werfplaats recruiters'
                                                                    : 'Open voorstel venster (klantportaal)'
                                                            }
                                                        >
                                                            <MonitorSmartphone size={14} />
                                                        </button>
                                                        )}
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isClientPortal) {
                                                                    window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Deze optie is alleen voor Werfplaats recruiters.", type: 'error' } }));
                                                                } else {
                                                                    handleRemoveApplication(cand.id, candidateName);
                                                                }
                                                            }}
                                                            className={`p-1.5 bg-white border border-slate-200 rounded shadow-sm transition-all ${isClientPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'hover:border-slate-300 hover:bg-red-50 text-slate-500 hover:text-red-600'}`}
                                                            title={isClientPortal ? "Alleen voor Werfplaats recruiters" : "Verwijderen"}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4" onClick={() => openCandidateDetailIfAllowed(cand)}>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border overflow-hidden shrink-0 bg-slate-100 border-slate-200 text-slate-600">
                                                            {cand.candidates?.photo_url || cand.candidates?.avatar_url ? (
                                                                <img src={cand.candidates.photo_url || cand.candidates.avatar_url} className="w-full h-full object-cover" />
                                                            ) : candidateName.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="min-w-0 flex flex-col justify-center">
                                                            <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#0073e6] transition-colors truncate">{candidateName}</h4>
                                                            <p className="text-xs font-medium text-slate-500 truncate max-w-[250px] flex items-center">
                                                                {cand.candidates?.candidate_number ? <span className="font-bold text-slate-400 mr-1.5 shrink-0">#{cand.candidates.candidate_number}</span> : null}
                                                                <span className="truncate">{roleDisplay}</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 hidden md:table-cell" onClick={(e) => { e.stopPropagation(); /* Prevents opening modal when clicking actionable items */ }}>
                                                    <div className="flex flex-col gap-2">
                                                        {candidateEmail && (
                                                            <button 
                                                                onClick={(e) => { 
                                                                    e.stopPropagation();
                                                                    if (isClientPortal) {
                                                                        window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Upgrade portal voor mail en whatsapp, contact Werfplaats.", type: 'error' } }));
                                                                    } else {
                                                                        setSelectedCandidateIds([cand.candidate_id]); 
                                                                        setShowBulkEmailModal(true); 
                                                                    }
                                                                }} 
                                                                className={`text-sm font-medium flex items-center gap-2 transition-colors w-fit text-left ${isClientPortal ? 'text-slate-300 cursor-not-allowed opacity-50 blur-[1px]' : 'text-slate-600 hover:text-[#0073e6]'}`}
                                                            >
                                                                <Mail size={14} className="text-slate-400 shrink-0" /> <span className="truncate">{candidateEmail}</span>
                                                            </button>
                                                        )}
                                                        {candidatePhone && (
                                                            <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                                                                <Phone size={14} className="text-slate-400 shrink-0" />
                                                                <span>{candidatePhone}</span>
                                                                <button 
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation(); 
                                                                        if (isClientPortal) {
                                                                            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Upgrade portal voor mail en whatsapp, contact Werfplaats.", type: 'error' } }));
                                                                        } else {
                                                                            setSelectedCandidateIds([cand.candidate_id]);
                                                                            setShowBulkWhatsAppModal(true);
                                                                        }
                                                                    }} 
                                                                    className={`transition-transform flex items-center justify-center p-1 rounded-md shrink-0 ${isClientPortal ? 'text-slate-300 bg-slate-100 cursor-not-allowed opacity-50 blur-[1px]' : 'text-[#25D366] bg-green-50 hover:scale-110'}`} 
                                                                    title={isClientPortal ? "Upgrade portal voor mail en whatsapp, contact Werfplaats." : "WhatsApp (bulk)"}
                                                                >
                                                                    <MessageCircle size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 hidden md:table-cell" onClick={() => openCandidateDetailIfAllowed(cand)}>
                                                    <span className="text-sm font-medium text-slate-600">
                                                        {new Date(cand.created_at).toLocaleString('nl-NL', {
                                                            day: '2-digit', month: '2-digit', year: 'numeric'
                                                        }).replace(',', '')}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <button 
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            if (isClientPortal) {
                                                                window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Deze optie is alleen voor Werfplaats recruiters.", type: 'error' } }));
                                                            } else {
                                                                handleOpenMoveModal(cand); 
                                                            }
                                                        }}
                                                        className="appearance-none bg-red-50 border border-slate-200 text-[#e51b23] text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-lg outline-none cursor-pointer hover:bg-red-100 hover:border-red-300 transition-colors shadow-sm flex items-center gap-2 w-full justify-between"
                                                    >
                                                        {displayVacancyApplicationStage(cand.status)}
                                                        <ChevronDown size={14} className="text-red-400" />
                                                    </button>
                                                </td>
                                                <td className="px-5 py-4 hidden xl:table-cell text-right" onClick={() => openCandidateDetailIfAllowed(cand)}>
                                                    <span className="text-xs font-bold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-md inline-block">
                                                        {cand.source_client?.name || cand.candidates?.source || 'Direct'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {selectedCandidateId && (
                <CandidateDetailModal
                    vacancyId={vacancyId}
                    candidateId={selectedCandidateId}
                    clientId={candidates.find(c => c.candidate_id === selectedCandidateId)?.client_id || 'system'}
                    onClose={() => { setSelectedCandidateId(null); setCandidateAction(undefined); }}
                    onUpdate={() => { loadCandidates(); }}
                    initialAction={candidateAction}
                />
            )}

            <BulkEmailComposerModal
                isOpen={showBulkEmailModal}
                onClose={() => {
                    setShowBulkEmailModal(false);
                    setSelectedCandidateIds([]);
                }}
                selectedCandidates={
                    candidates
                        .filter(c => selectedCandidateIds.includes(c.candidate_id))
                        .map(c => ({
                            id: c.candidate_id,
                            name: c.candidates?.name || 'Onbekend',
                            email: c.candidates?.email || ''
                        }))
                }
                vacancyData={{ title: "Shortlist Vacature", id: vacancyId }}
            />

            <BulkWhatsAppComposerModal
                isOpen={showBulkWhatsAppModal}
                onClose={() => {
                    setShowBulkWhatsAppModal(false);
                    setSelectedCandidateIds([]);
                }}
                selectedCandidates={
                    candidates
                        .filter(c => selectedCandidateIds.includes(c.candidate_id))
                        .map(c => ({
                            id: c.candidate_id,
                            name: c.candidates?.name || 'Onbekend',
                            email: c.candidates?.email || '',
                            phone: c.candidates?.phone || ''
                        }))
                }
                vacancyData={{ title: "Shortlist Vacature", id: vacancyId }}
            />

            <CandidateMoveModal
                isOpen={showMoveModal}
                onClose={() => setShowMoveModal(false)}
                moveDetails={moveDetails}
                templates={templates}
                currentUser={{ id: undefined }} // Or pass user from Dashboard down. Optional.
                onConfirm={handleConfirmShortlistMove}
            />
        </div>
    );
};

export default VacancyFiltersTab;


